import React, { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext();

export const translations = {
  en: {
    // Navigation
    nav: {
      home: 'Home',
      services: 'Services',
      developments: 'Developments',
      clients: 'Clients',
      relocationHub: 'Relocation Hub',
      contact: 'Contact',
      experience: 'Experience',
      partners: 'Partners',
      caseStudies: 'Case Studies',
      insights: 'Insights',
      portal: 'Client Portal',
      login: 'Login',
      logout: 'Logout',
      dashboard: 'Dashboard'
    },
    // Home page
    home: {
      heroTitle: 'Cross Border Development and Presale Strategy',
      heroSubtitle: 'Structured positioning, marketing, and sales execution for high stakes real estate projects across the United States and Mexico.',
      ctaDevelopment: 'Request Development Consultation',
      ctaRelocation: 'Relocating? Explore the Relocation Hub',
      trustCrossBorder: 'Cross Border Execution',
      trustPresales: 'Structured Presales',
      trustPartner: 'Verified Partner Network',
      trustProcess: 'Institutional Process',
      forDevelopers: 'For Developers',
      developmentStrategy: 'Development Strategy',
      developmentDesc: 'Positioning, presale strategy, broker activation, sales operations.',
      exploreDevServices: 'Explore Development Services',
      forRelocators: 'For Relocators',
      relocationPlanning: 'Relocation Planning',
      relocationDesc: 'Structured relocation planning, curated housing, verified providers.',
      startRelocation: 'Start CDMX Relocation Plan',
      caseStudies: 'Case Studies',
      selectedWork: 'Selected Work',
      selectedWorkDesc: 'Review all the projects we have had the pleasure of working on.',
      viewAllCaseStudies: 'View All Case Studies',
      readyToBegin: 'Ready to Begin?',
      readyToBeginDesc: 'Contact us to discuss how we can support your real estate development or relocation.',
      getInTouch: 'Get In Touch'
    },
    // Services page
    services: {
      title: 'Services',
      heroTitle: 'Development and Presale Strategy',
      heroDesc: 'Comprehensive services for real estate developments requiring institutional positioning, structured marketing, and sales execution.',
      intelligence: 'Intelligence',
      planning: 'Planning',
      branding: 'Branding',
      marketing: 'Marketing',
      development: 'Development',
      salesOperations: 'Sales and Operations',
      workWithUs: 'Work With Us',
      workWithUsDesc: 'Tell us about your project and how we can support your development.',
      projectType: 'Project Type',
      location: 'Location',
      projectPhase: 'Project Phase',
      timeline: 'Timeline',
      budgetRange: 'Budget Range',
      howDidYouHear: 'How did you hear about us?',
      name: 'Name',
      email: 'Email',
      phone: 'Phone',
      projectDetails: 'Project Details',
      requestConsultation: 'Request Consultation',
      sending: 'Sending...',
      selectType: 'Select type',
      selectPhase: 'Select phase',
      selectTimeline: 'Select timeline',
      residential: 'Residential',
      mixedUse: 'Mixed Use',
      commercial: 'Commercial',
      hospitality: 'Hospitality',
      brandedResidence: 'Branded Residence',
      planningPhase: 'Planning',
      preConstruction: 'Pre-Construction',
      underConstruction: 'Under Construction',
      readyForPresales: 'Ready for Presales',
      immediate: 'Immediate',
      months13: '1-3 Months',
      months36: '3-6 Months',
      months612: '6-12 Months',
      preferNotToSay: 'Prefer not to say'
    },
    // Contact page
    contact: {
      title: 'Contact',
      subtitle: 'Tell us about your needs and we will respond within 24 hours.',
      name: 'Name',
      email: 'Email',
      phone: 'Phone',
      inquiryType: 'Inquiry Type',
      message: 'Message',
      selectedPlan: 'Selected Plan',
      sendInquiry: 'Send Inquiry',
      sending: 'Sending...',
      responseTime: 'We typically respond within 24 hours. For urgent matters, please call or WhatsApp.',
      selectType: 'Select type',
      developmentProject: 'Development Project',
      relocation: 'Relocation',
      buyerRepresentation: 'Buyer Representation',
      general: 'General',
      yourName: 'Your name',
      yourEmail: 'your@email.com',
      phonePlaceholder: '+1 (555) 000-0000',
      messagePlaceholder: 'Tell us about your project or needs...',
      emailLabel: 'Email',
      phoneUS: 'Phone (US)',
      whatsAppMX: 'WhatsApp (MX)'
    },
    // Relocation page
    relocation: {
      heroLabel: 'Mexico',
      heroTitle: 'Relocating to Mexico',
      heroSubtitle: 'Structured guidance for housing, schools, visas, healthcare, and daily life-without the guesswork.',
      exploreGuide: 'Explore the Relocation Guide',
      supportOptions: 'Structured Support Options',
      guideTitle: 'Relocation Guide',
      guideDesc: 'Everything you need to plan a successful move to Mexico.',
      structuredSupportTitle: 'If You Prefer Structured Support',
      structuredSupportDesc: 'For those who prefer personalized guidance and coordination, the plans below formalize scope and timelines.',
      proceedToIntake: 'Proceed to Intake',
      creditNote: 'Relocation fees may be credited toward representation if we assist with your lease or purchase.',
      faqTitle: 'Common Questions',
      learnMore: 'Learn more'
    },
    // Thank you page
    thankYou: {
      title: 'Thank You',
      paymentConfirmed: 'Payment Confirmed',
      processing: 'We\'re processing your request. You\'ll receive a confirmation email shortly.',
      nextStep: 'Thank You. Next Step.',
      received: 'We\'ve received your inquiry and will respond within 24 hours.',
      confirmingPayment: 'Confirming your payment...',
      scheduleCall: 'Schedule a Call',
      bookOnboarding: 'Book your onboarding call directly on our calendar.',
      bookSooner: 'Need to speak sooner? Book a time directly on our calendar.',
      messageWhatsApp: 'Message Us on WhatsApp',
      returnHome: 'Return to Homepage',
      backHome: 'Back to Home'
    },
    // Intake form
    intake: {
      title: 'Relocation Intake',
      subtitle: 'Tell us about your relocation plans so we can provide the best support.',
      step1: 'Intake',
      step2: 'Confirm',
      step3: 'Payment',
      selectPlan: 'Select Plan',
      name: 'Name',
      email: 'Email',
      phone: 'Phone',
      currentLocation: 'Current Location',
      cityCountry: 'City, Country',
      timeline: 'Timeline',
      selectTimeline: 'Select timeline',
      householdSize: 'Household Size',
      selectSize: 'Select size',
      additionalNotes: 'Additional Notes',
      notesPlaceholder: 'Tell us about your specific needs, concerns, or questions...',
      continueToConfirm: 'Continue to Confirmation',
      submitting: 'Submitting...',
      confirmTitle: 'Confirm Your Plan',
      confirmSubtitle: 'Review your selection before proceeding to payment.',
      selectedPlan: 'Selected Plan',
      whatsIncluded: 'What\'s Included:',
      yourInfo: 'Your Information:',
      confirmSecure: 'Confirm & Secure Plan',
      redirectingPayment: 'Redirecting to Payment...',
      backToEdit: '← Back to Edit',
      securePayment: 'Secure payment processed by Stripe. Supports card, Apple Pay, and Google Pay.',
      backToRelocation: '← Back to Relocation',
      single: 'Single',
      couple: 'Couple',
      familySmall: 'Family (1-2 children)',
      familyLarge: 'Family (3+ children)',
      months13: '1-3 months',
      months36: '3-6 months',
      months612: '6-12 months',
      justResearching: 'Just researching'
    },
    // Client Portal
    portal: {
      welcome: 'Welcome to Your Portal',
      login: 'Login',
      loginWith: 'Sign in with Google',
      loginDesc: 'Access your personalized dashboard, communications, and resources.',
      dashboard: 'Dashboard',
      profile: 'Profile',
      listings: 'Listings',
      communications: 'Communications',
      documents: 'Documents',
      resources: 'Resources',
      progress: 'Progress Tracker',
      payments: 'Payment History',
      calendar: 'Calendar',
      logout: 'Logout',
      noListings: 'No listings yet',
      addListing: 'Add Listing',
      viewAll: 'View All'
    },
    // Partners page
    partners: {
      title: 'Trusted Partners',
      subtitle: 'A curated network of vetted professionals who share our standards for cross-border real estate.',
      categories: {
        legal: 'Legal & Immigration',
        financial: 'Financial & Tax',
        property: 'Property & Lifestyle'
      },
      vetted: 'Vetted',
      requestIntro: 'Request Introduction',
      contact: 'Contact'
    },
    // Insights page
    insights: {
      title: 'Insights',
      subtitle: 'Analysis and perspectives on cross-border real estate, relocation, and market trends.',
      allArticles: 'All Articles',
      readMore: 'Read More',
      publishedOn: 'Published on',
      byAuthor: 'By',
      minRead: 'min read',
      categories: {
        all: 'All',
        market: 'Market Analysis',
        relocation: 'Relocation',
        legal: 'Legal & Tax',
        investment: 'Investment'
      },
      backToInsights: 'Back to Insights',
      shareArticle: 'Share this article',
      relatedArticles: 'Related Articles'
    },
    // Experience page
    experience: {
      title: 'Experience',
      subtitle: 'Over a decade of cross-border real estate expertise across Mexico and the United States.',
      yearsExperience: 'Years of Experience',
      transactionsCompleted: 'Transactions Completed',
      clientsSatisfied: 'Clients Satisfied',
      expertise: 'Areas of Expertise',
      background: 'Background',
      timeline: 'Career Timeline'
    },
    // Developments page
    developments: {
      title: 'Developments',
      subtitle: 'Featured real estate developments we are currently marketing or have successfully sold.',
      featured: 'Featured',
      status: {
        active: 'Active',
        soldOut: 'Sold Out',
        comingSoon: 'Coming Soon'
      },
      units: 'Units',
      location: 'Location',
      viewProject: 'View Project',
      inquire: 'Inquire About This Project'
    },
    // Case Study page
    caseStudy: {
      overview: 'Overview',
      challenge: 'The Challenge',
      solution: 'Our Solution',
      results: 'Results',
      testimonial: 'Client Testimonial',
      projectDetails: 'Project Details',
      timeline: 'Timeline',
      scope: 'Scope',
      viewAllCases: 'View All Case Studies',
      nextProject: 'Next Project',
      prevProject: 'Previous Project'
    },
    // Guide Download page
    guideDownload: {
      title: 'Your Guide is Ready',
      thankYou: 'Thank you for your interest. Click below to download your guide.',
      downloadGuide: 'Download Guide',
      pdfDocument: 'PDF Document',
      linkExpires: 'Link expires in',
      downloadError: 'Download Error',
      linkExpired: 'Link Expired',
      expiredMessage: 'This download link has expired. Please request a new one.',
      requestNewLink: 'Request New Download Link',
      whatsNext: "What's Next?",
      viewPackages: 'View Support Packages',
      scheduleCall: 'Schedule a Call',
      moreResources: 'More Resources',
      housingGuide: 'Housing Guide',
      housingDesc: 'Find your perfect neighborhood in CDMX',
      visaGuide: 'Visa & Residency',
      visaDesc: 'Immigration pathways explained',
      trustedPartners: 'Trusted Partners',
      partnersDesc: 'Vetted professionals for your move'
    },
    // Footer
    footer: {
      tagline: 'Cross border real estate advisory for developments and relocations.',
      contact: 'Contact',
      services: 'Services',
      developmentAdvisory: 'Development Advisory',
      allServices: 'All Services',
      company: 'Company',
      legal: 'Legal',
      legalText: 'Licensed Real Estate Broker in Mexico. Not an attorney. No legal or tax advice provided.',
      copyright: '© {year} The Mundalo. All rights reserved.'
    },
    // Common
    common: {
      learnMore: 'Learn More',
      backTo: 'Back to',
      viewDetails: 'View Details',
      loading: 'Loading...',
      error: 'An error occurred',
      submit: 'Submit',
      cancel: 'Cancel',
      save: 'Save',
      delete: 'Delete',
      edit: 'Edit',
      close: 'Close',
      required: 'Required'
    }
  },
  es: {
    // Navigation
    nav: {
      home: 'Inicio',
      services: 'Servicios',
      developments: 'Desarrollos',
      clients: 'Clientes',
      relocationHub: 'Hub de Reubicación',
      contact: 'Contacto',
      experience: 'Experiencia',
      partners: 'Socios',
      caseStudies: 'Casos de Estudio',
      insights: 'Perspectivas',
      portal: 'Portal de Clientes',
      login: 'Iniciar Sesión',
      logout: 'Cerrar Sesión',
      dashboard: 'Panel'
    },
    // Home page
    home: {
      heroTitle: 'Estrategia de Desarrollo y Preventa Transfronteriza',
      heroSubtitle: 'Posicionamiento estructurado, marketing y ejecución de ventas para proyectos inmobiliarios de alto nivel en Estados Unidos y México.',
      ctaDevelopment: 'Solicitar Consulta de Desarrollo',
      ctaRelocation: '¿Te mudas? Explora el Hub de Reubicación',
      trustCrossBorder: 'Ejecución Transfronteriza',
      trustPresales: 'Preventas Estructuradas',
      trustPartner: 'Red de Socios Verificados',
      trustProcess: 'Proceso Institucional',
      forDevelopers: 'Para Desarrolladores',
      developmentStrategy: 'Estrategia de Desarrollo',
      developmentDesc: 'Posicionamiento, estrategia de preventa, activación de corredores, operaciones de venta.',
      exploreDevServices: 'Explorar Servicios de Desarrollo',
      forRelocators: 'Para Reubicaciones',
      relocationPlanning: 'Planificación de Reubicación',
      relocationDesc: 'Planificación estructurada de reubicación, vivienda curada, proveedores verificados.',
      startRelocation: 'Iniciar Plan de Reubicación CDMX',
      caseStudies: 'Casos de Estudio',
      selectedWork: 'Trabajo Seleccionado',
      selectedWorkDesc: 'Revisa todos los proyectos en los que hemos tenido el placer de trabajar.',
      viewAllCaseStudies: 'Ver Todos los Casos de Estudio',
      readyToBegin: '¿Listo para Comenzar?',
      readyToBeginDesc: 'Contáctanos para discutir cómo podemos apoyar tu desarrollo inmobiliario o reubicación.',
      getInTouch: 'Contáctanos'
    },
    // Services page
    services: {
      title: 'Servicios',
      heroTitle: 'Estrategia de Desarrollo y Preventa',
      heroDesc: 'Servicios integrales para desarrollos inmobiliarios que requieren posicionamiento institucional, marketing estructurado y ejecución de ventas.',
      intelligence: 'Inteligencia',
      planning: 'Planificación',
      branding: 'Marca',
      marketing: 'Marketing',
      development: 'Desarrollo',
      salesOperations: 'Ventas y Operaciones',
      workWithUs: 'Trabaja Con Nosotros',
      workWithUsDesc: 'Cuéntanos sobre tu proyecto y cómo podemos apoyar tu desarrollo.',
      projectType: 'Tipo de Proyecto',
      location: 'Ubicación',
      projectPhase: 'Fase del Proyecto',
      timeline: 'Cronograma',
      budgetRange: 'Rango de Presupuesto',
      howDidYouHear: '¿Cómo nos conociste?',
      name: 'Nombre',
      email: 'Correo Electrónico',
      phone: 'Teléfono',
      projectDetails: 'Detalles del Proyecto',
      requestConsultation: 'Solicitar Consulta',
      sending: 'Enviando...',
      selectType: 'Seleccionar tipo',
      selectPhase: 'Seleccionar fase',
      selectTimeline: 'Seleccionar cronograma',
      residential: 'Residencial',
      mixedUse: 'Uso Mixto',
      commercial: 'Comercial',
      hospitality: 'Hospitalidad',
      brandedResidence: 'Residencia de Marca',
      planningPhase: 'Planificación',
      preConstruction: 'Pre-Construcción',
      underConstruction: 'En Construcción',
      readyForPresales: 'Listo para Preventas',
      immediate: 'Inmediato',
      months13: '1-3 Meses',
      months36: '3-6 Meses',
      months612: '6-12 Meses',
      preferNotToSay: 'Prefiero no decir'
    },
    // Contact page
    contact: {
      title: 'Contacto',
      subtitle: 'Cuéntanos sobre tus necesidades y te responderemos en 24 horas.',
      name: 'Nombre',
      email: 'Correo Electrónico',
      phone: 'Teléfono',
      inquiryType: 'Tipo de Consulta',
      message: 'Mensaje',
      selectedPlan: 'Plan Seleccionado',
      sendInquiry: 'Enviar Consulta',
      sending: 'Enviando...',
      responseTime: 'Normalmente respondemos en 24 horas. Para asuntos urgentes, llámanos o escribe por WhatsApp.',
      selectType: 'Seleccionar tipo',
      developmentProject: 'Proyecto de Desarrollo',
      relocation: 'Reubicación',
      buyerRepresentation: 'Representación de Comprador',
      general: 'General',
      yourName: 'Tu nombre',
      yourEmail: 'tu@correo.com',
      phonePlaceholder: '+1 (555) 000-0000',
      messagePlaceholder: 'Cuéntanos sobre tu proyecto o necesidades...',
      emailLabel: 'Correo',
      phoneUS: 'Teléfono (US)',
      whatsAppMX: 'WhatsApp (MX)'
    },
    // Relocation page
    relocation: {
      heroLabel: 'México',
      heroTitle: 'Reubicación a México',
      heroSubtitle: 'Guía estructurada para vivienda, escuelas, visas, salud y vida diaria-sin conjeturas.',
      exploreGuide: 'Explorar la Guía de Reubicación',
      supportOptions: 'Opciones de Soporte Estructurado',
      guideTitle: 'Guía de Reubicación',
      guideDesc: 'Todo lo que necesitas para planificar una mudanza exitosa a México.',
      structuredSupportTitle: 'Si Prefieres Soporte Estructurado',
      structuredSupportDesc: 'Para quienes prefieren orientación personalizada y coordinación, los planes a continuación formalizan alcance y tiempos.',
      proceedToIntake: 'Proceder a Registro',
      creditNote: 'Las tarifas de reubicación pueden acreditarse hacia representación si asistimos con tu arrendamiento o compra.',
      faqTitle: 'Preguntas Frecuentes',
      learnMore: 'Más información'
    },
    // Thank you page
    thankYou: {
      title: 'Gracias',
      paymentConfirmed: 'Pago Confirmado',
      processing: 'Estamos procesando tu solicitud. Recibirás un correo de confirmación en breve.',
      nextStep: 'Gracias. Siguiente Paso.',
      received: 'Hemos recibido tu consulta y te responderemos en 24 horas.',
      confirmingPayment: 'Confirmando tu pago...',
      scheduleCall: 'Agenda una Llamada',
      bookOnboarding: 'Reserva tu llamada de incorporación directamente en nuestro calendario.',
      bookSooner: '¿Necesitas hablar antes? Reserva un horario directamente en nuestro calendario.',
      messageWhatsApp: 'Escríbenos por WhatsApp',
      returnHome: 'Volver al Inicio',
      backHome: 'Volver al Inicio'
    },
    // Intake form
    intake: {
      title: 'Registro de Reubicación',
      subtitle: 'Cuéntanos sobre tus planes de reubicación para brindarte el mejor soporte.',
      step1: 'Registro',
      step2: 'Confirmar',
      step3: 'Pago',
      selectPlan: 'Seleccionar Plan',
      name: 'Nombre',
      email: 'Correo Electrónico',
      phone: 'Teléfono',
      currentLocation: 'Ubicación Actual',
      cityCountry: 'Ciudad, País',
      timeline: 'Cronograma',
      selectTimeline: 'Seleccionar cronograma',
      householdSize: 'Tamaño del Hogar',
      selectSize: 'Seleccionar tamaño',
      additionalNotes: 'Notas Adicionales',
      notesPlaceholder: 'Cuéntanos sobre tus necesidades específicas, preocupaciones o preguntas...',
      continueToConfirm: 'Continuar a Confirmación',
      submitting: 'Enviando...',
      confirmTitle: 'Confirma Tu Plan',
      confirmSubtitle: 'Revisa tu selección antes de proceder al pago.',
      selectedPlan: 'Plan Seleccionado',
      whatsIncluded: 'Qué Incluye:',
      yourInfo: 'Tu Información:',
      confirmSecure: 'Confirmar y Asegurar Plan',
      redirectingPayment: 'Redirigiendo al Pago...',
      backToEdit: '← Volver a Editar',
      securePayment: 'Pago seguro procesado por Stripe. Acepta tarjeta, Apple Pay y Google Pay.',
      backToRelocation: '← Volver a Reubicación',
      single: 'Soltero/a',
      couple: 'Pareja',
      familySmall: 'Familia (1-2 hijos)',
      familyLarge: 'Familia (3+ hijos)',
      months13: '1-3 meses',
      months36: '3-6 meses',
      months612: '6-12 meses',
      justResearching: 'Solo investigando'
    },
    // Client Portal
    portal: {
      welcome: 'Bienvenido a Tu Portal',
      login: 'Iniciar Sesión',
      loginWith: 'Iniciar sesión con Google',
      loginDesc: 'Accede a tu panel personalizado, comunicaciones y recursos.',
      dashboard: 'Panel',
      profile: 'Perfil',
      listings: 'Propiedades',
      communications: 'Comunicaciones',
      documents: 'Documentos',
      resources: 'Recursos',
      progress: 'Seguimiento de Progreso',
      payments: 'Historial de Pagos',
      calendar: 'Calendario',
      logout: 'Cerrar Sesión',
      noListings: 'Sin propiedades aún',
      addListing: 'Agregar Propiedad',
      viewAll: 'Ver Todo'
    },
    // Partners page
    partners: {
      title: 'Socios de Confianza',
      subtitle: 'Una red curada de profesionales verificados que comparten nuestros estándares para bienes raíces transfronterizos.',
      categories: {
        legal: 'Legal e Inmigración',
        financial: 'Financiero e Impuestos',
        property: 'Propiedad y Estilo de Vida'
      },
      vetted: 'Verificado',
      requestIntro: 'Solicitar Presentación',
      contact: 'Contactar'
    },
    // Insights page
    insights: {
      title: 'Perspectivas',
      subtitle: 'Análisis y perspectivas sobre bienes raíces transfronterizos, reubicación y tendencias del mercado.',
      allArticles: 'Todos los Artículos',
      readMore: 'Leer Más',
      publishedOn: 'Publicado el',
      byAuthor: 'Por',
      minRead: 'min de lectura',
      categories: {
        all: 'Todos',
        market: 'Análisis de Mercado',
        relocation: 'Reubicación',
        legal: 'Legal e Impuestos',
        investment: 'Inversión'
      },
      backToInsights: 'Volver a Perspectivas',
      shareArticle: 'Compartir este artículo',
      relatedArticles: 'Artículos Relacionados'
    },
    // Experience page
    experience: {
      title: 'Experiencia',
      subtitle: 'Más de una década de experiencia en bienes raíces transfronterizos en México y Estados Unidos.',
      yearsExperience: 'Años de Experiencia',
      transactionsCompleted: 'Transacciones Completadas',
      clientsSatisfied: 'Clientes Satisfechos',
      expertise: 'Áreas de Especialización',
      background: 'Trayectoria',
      timeline: 'Línea de Tiempo Profesional'
    },
    // Developments page
    developments: {
      title: 'Desarrollos',
      subtitle: 'Desarrollos inmobiliarios destacados que estamos comercializando actualmente o que hemos vendido exitosamente.',
      featured: 'Destacado',
      status: {
        active: 'Activo',
        soldOut: 'Vendido',
        comingSoon: 'Próximamente'
      },
      units: 'Unidades',
      location: 'Ubicación',
      viewProject: 'Ver Proyecto',
      inquire: 'Consultar Sobre Este Proyecto'
    },
    // Case Study page
    caseStudy: {
      overview: 'Resumen',
      challenge: 'El Desafío',
      solution: 'Nuestra Solución',
      results: 'Resultados',
      testimonial: 'Testimonio del Cliente',
      projectDetails: 'Detalles del Proyecto',
      timeline: 'Cronograma',
      scope: 'Alcance',
      viewAllCases: 'Ver Todos los Casos de Estudio',
      nextProject: 'Siguiente Proyecto',
      prevProject: 'Proyecto Anterior'
    },
    // Guide Download page
    guideDownload: {
      title: 'Tu Guía Está Lista',
      thankYou: 'Gracias por tu interés. Haz clic abajo para descargar tu guía.',
      downloadGuide: 'Descargar Guía',
      pdfDocument: 'Documento PDF',
      linkExpires: 'El enlace expira en',
      downloadError: 'Error de Descarga',
      linkExpired: 'Enlace Expirado',
      expiredMessage: 'Este enlace de descarga ha expirado. Por favor solicita uno nuevo.',
      requestNewLink: 'Solicitar Nuevo Enlace',
      whatsNext: '¿Qué Sigue?',
      viewPackages: 'Ver Paquetes de Soporte',
      scheduleCall: 'Agendar una Llamada',
      moreResources: 'Más Recursos',
      housingGuide: 'Guía de Vivienda',
      housingDesc: 'Encuentra tu vecindario perfecto en CDMX',
      visaGuide: 'Visa y Residencia',
      visaDesc: 'Rutas de inmigración explicadas',
      trustedPartners: 'Socios de Confianza',
      partnersDesc: 'Profesionales verificados para tu mudanza'
    },
    // Footer
    footer: {
      tagline: 'Asesoría inmobiliaria transfronteriza para desarrollos y reubicaciones.',
      contact: 'Contacto',
      services: 'Servicios',
      developmentAdvisory: 'Asesoría de Desarrollo',
      allServices: 'Todos los Servicios',
      company: 'Empresa',
      legal: 'Legal',
      legalText: 'Corredor de bienes raíces con licencia en México. No es abogado. No se proporciona asesoría legal o fiscal.',
      copyright: '© {year} The Mundalo. Todos los derechos reservados.'
    },
    // Common
    common: {
      learnMore: 'Más Información',
      backTo: 'Volver a',
      viewDetails: 'Ver Detalles',
      loading: 'Cargando...',
      error: 'Ocurrió un error',
      submit: 'Enviar',
      cancel: 'Cancelar',
      save: 'Guardar',
      delete: 'Eliminar',
      edit: 'Editar',
      close: 'Cerrar',
      required: 'Requerido'
    }
  }
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    // Check localStorage for saved language preference
    if (typeof window !== 'undefined') {
      return localStorage.getItem('frg_language') || 'en';
    }
    return 'en';
  });

  useEffect(() => {
    // Save language preference to localStorage
    localStorage.setItem('frg_language', language);
    // iter 107 - Reflect the active language on <html lang="…"> so screen
    // readers, browser auto-translate UIs, and Google's SEO crawler all
    // pick up the change. Pure side-effect; safe under SSR (guarded).
    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute('lang', language);
    }
  }, [language]);

  // iter 107 - Translation lookup with English fallback.
  //
  // Bug: previously `t(key)` returned the raw key (e.g. `"nav.home"`) when
  // the active language was missing that string. ES users saw broken keys
  // all over the UI the moment they flipped the toggle.
  //
  // Fix: when the active-language path misses, fall back to the same key
  // in the English tree before giving up. Only return the raw key if BOTH
  // languages are missing it - which means a real authoring bug worth
  // surfacing in dev.
  const t = (key) => {
    const keys = key.split('.');
    const resolve = (lang) => {
      let value = translations[lang];
      for (const k of keys) {
        value = value?.[k];
        if (value === undefined) return undefined;
      }
      return value;
    };
    const active = resolve(language);
    if (active !== undefined && active !== null && active !== '') return active;
    const en = resolve('en');
    if (en !== undefined && en !== null && en !== '') return en;
    // Last resort - strip the namespace prefix so the screen at least
    // shows something human-readable instead of `nav.something`.
    return key.split('.').pop().replace(/[_-]/g, ' ');
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, translations }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
